import type {
  Demand2PerformanceSummary,
  Demand2WindowStats,
  VariantPerfStats,
} from "~/components/demand-2/shared/useDemand2Performance"
import type { RouterOutputs } from "~/utils/api"

type PerfStatsRow = RouterOutputs["demand2"]["getPerformanceStats"][number]

const summarize = (entries: Iterable<VariantPerfStats>): Demand2PerformanceSummary => {
  let totalExpectedDemand = 0
  let totalSales = 0
  let totalReturns = 0
  let totalRevenue = 0
  let totalCost = 0
  for (const e of entries) {
    totalExpectedDemand += e.totalExpectedDemand
    totalSales += e.totalSales
    totalReturns += e.totalReturns
    totalRevenue += e.totalRevenue
    totalCost += e.totalCost
  }
  return { totalExpectedDemand, totalSales, totalReturns, totalRevenue, totalCost }
}

/**
 * Expected-side fields are NULL under a channel filter (ForecastVariantHistoric has no channel
 * column). Coerced to 0 to keep arithmetic NaN-free; these zeros mean "not offered", not "no
 * demand", so callers gate on `channelSelected`, never on the value.
 */
const toStats = (
  s: PerfStatsRow["dateRanges"][string] &
    Partial<Pick<PerfStatsRow, "firstWeek" | "lastWeek" | "basis">>,
): VariantPerfStats => ({
  totalExpectedDemand: s.totalExpectedDemand ?? 0,
  totalSales: s.totalSales,
  totalReturns: s.totalReturns,
  totalRevenue: s.totalRevenue,
  totalCost: s.totalCost,
  overlapPeriods: s.overlapPeriods ?? 0,
  firstWeek: s.firstWeek ?? null,
  lastWeek: s.lastWeek ?? null,
  basis: s.basis ?? null,
})

/** Per-variant lookback in whole weeks (1–12); `min`/`max` span the displayed variants. */
export type Demand2Lookback = { weekDiffs: number[]; min: number | null; max: number | null }

export function projectPerfStats(
  rows: PerfStatsRow[],
  displayVariantIds: string[],
  windows: number[] | undefined,
): {
  statsByVariant: Map<string, VariantPerfStats>
  summary: Demand2PerformanceSummary
  byWindow: Map<number, Demand2WindowStats>
  dateRanges: Map<string, Demand2WindowStats>
  lookback: Demand2Lookback
} {
  const display = new Set(displayVariantIds)
  const filteredRows = rows.filter((r) => display.has(r.variantId))
  const statsByVariant = new Map<string, VariantPerfStats>()
  const byRange = new Map<string, Map<string, VariantPerfStats>>()
  const weekDiffSet = new Set<number>()
  for (const r of filteredRows) {
    statsByVariant.set(r.variantId, toStats(r))
    for (const [key, stats] of Object.entries(r.dateRanges)) {
      let m = byRange.get(key)
      if (!m) byRange.set(key, (m = new Map()))
      m.set(r.variantId, toStats(stats))
    }
    if (r.weekDiff != null) weekDiffSet.add(r.weekDiff)
  }
  const byWindow = new Map<number, Demand2WindowStats>()
  for (const weeks of windows ?? []) {
    const m = new Map<string, VariantPerfStats>()
    for (const r of filteredRows) {
      const w = r.byWindow[String(weeks)]
      if (w) m.set(r.variantId, toStats(w))
    }
    byWindow.set(weeks, { statsByVariant: m, summary: summarize(m.values()) })
  }
  const weekDiffs = [...weekDiffSet].sort((a, b) => a - b)
  return {
    statsByVariant,
    summary: summarize(statsByVariant.values()),
    byWindow,
    dateRanges: new Map(
      [...byRange].map(([key, m]) => [key, { statsByVariant: m, summary: summarize(m.values()) }]),
    ),
    lookback: {
      weekDiffs,
      min: weekDiffs[0] ?? null,
      max: weekDiffs[weekDiffs.length - 1] ?? null,
    },
  }
}
